import { colors } from '@styles/colors'
import { useStyletron } from 'baseui'
import { Input, InputOverrides } from 'baseui/input'
import { forwardRef } from 'react'
import { LabelS } from '../base-typography'
import { useOverrides } from '../hooks/use-overrides'
import { Icon, IconType } from '../icon'
import { BaseInputProps } from '.'
import { useInputStyle } from './use-input-style'
import { useInputOverrides } from './use-input-overrides'

export const BaseInput = forwardRef<Input, BaseInputProps>(
  (
    {
      overrides,
      leading,
      trailing,
      action,
      onAction,
      error,
      positive,
      disabled,
      spaceTop,
      spaceBottom,
      label,
      size = 'l',
      ...props
    },
    ref
  ) => {
    const { getIconColor } = useInputStyle()
    const { inputOverrides } = useInputOverrides({
      spaceTop,
      spaceBottom,
      size,
      label,
    })

    return (
      <>
        <Input
          overrides={useOverrides<InputOverrides>(inputOverrides, overrides)}
          startEnhancer={
            leading &&
            ((props) => (
              <Icon type={leading} size={16} color={getIconColor(props)} />
            ))
          }
          endEnhancer={
            trailing &&
            ((props) => (
              <Icon type={trailing} size={16} color={getIconColor(props)} />
            ))
          }
          error={Boolean(error)}
          positive={Boolean(positive)}
          disabled={disabled}
          maxLength={100}
          {...props}
          ref={ref}
        />
        {!disabled && error && <Hint msg={error} type="error" />}
        {!disabled && positive && <Hint msg={positive} type="positive" />}
      </>
    )
  }
)

interface HintProps {
  msg: string
  type: 'error' | 'positive'
}

export const Hint = ({ msg, type }: HintProps) => {
  const [css] = useStyletron()

  const style = {
    error: {
      color: colors.red400,
      icon: 'Spam2Line' as IconType,
    },
    positive: {
      color: colors.green400,
      icon: 'CheckDoubleFill' as IconType,
    },
  }[type]

  return (
    <span
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: '4px',
      })}
    >
      <Icon type={style.icon} size={16} color={style.color} />
      <LabelS color={style.color} marginLeft="4px">
        {msg}
      </LabelS>
    </span>
  )
}
